import React, { useEffect, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { createReceipt, getCategories } from '../lib/api';
import { useManualEntryForm } from '../hooks/useManualEntryForm';

export default function ManualEntryModal({ onClose, onSaved }) {
  const { colors } = useTheme();
  const {
    form, setField, setTotal,
    autoTotal, totalDisplay,
    isValid, isoDateOk, toPayload,
  } = useManualEntryForm();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    getCategories()
      .then((r) => setCats(r.categories.filter((c) => !c.is_special)))
      .catch((err) => {
        console.error('Failed to load categories for manual entry:', err);
        setCats([]);
      });
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!isValid || saving) return;
    setSaving(true); setError(null);
    try {
      await createReceipt(toPayload());
      onSaved?.();
    } catch (e2) {
      setError(e2?.detail || 'Couldn\'t save — please retry');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
      data-testid="manual-entry-modal-backdrop">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        data-testid="manual-entry-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: colors.border }}>
          <h2 className="text-base font-semibold" style={{ color: colors.text }}>
            Add expense manually
          </h2>
          <button onClick={onClose} data-testid="manual-close"
            className="p-1 rounded hover:opacity-70" style={{ color: colors.textMuted }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error ? (
            <div className="text-xs px-3 py-2 rounded"
              style={{ backgroundColor: colors.errorSoft, color: colors.error }}>
              {error}
            </div>
          ) : null}

          <Field label="Vendor *" colors={colors}>
            <input data-testid="mm-vendor" value={form.vendor}
              onChange={(e) => setField('vendor', e.target.value)}
              placeholder="Store or vendor name"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }} />
          </Field>
          <Field label="Date *" colors={colors}>
            <input data-testid="mm-date" value={form.date}
              onChange={(e) => setField('date', e.target.value)}
              type="date"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${isoDateOk(form.date) ? colors.border : colors.error}` }} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Subtotal *" colors={colors}>
              <NumInput testID="mm-subtotal" value={form.subtotal}
                onChange={(v) => setField('subtotal', v)} colors={colors} />
            </Field>
            <Field label="GST (TPS)" colors={colors}>
              <NumInput testID="mm-gst" value={form.gst}
                onChange={(v) => setField('gst', v)} colors={colors} />
            </Field>
            <Field label="QST (TVQ)" colors={colors}>
              <NumInput testID="mm-qst" value={form.qst}
                onChange={(v) => setField('qst', v)} colors={colors} />
            </Field>
            <Field label="Total *" colors={colors}>
              <div className="relative">
                <NumInput testID="mm-total" value={totalDisplay}
                  onChange={setTotal} colors={colors} />
                {!form.totalTouched && autoTotal ? (
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full pointer-events-none"
                    style={{ backgroundColor: colors.accentSoft, color: colors.accent }}>
                    auto
                  </span>
                ) : null}
              </div>
            </Field>
          </div>

          <Field label="Category *" colors={colors}>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
              {cats.map((c) => {
                const sel = form.category === c.name;
                return (
                  <button key={c.id} type="button" data-testid={`mm-cat-${c.name}`}
                    onClick={() => setField('category', c.name)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border"
                    style={{
                      backgroundColor: sel ? colors.accent : colors.surface,
                      color: sel ? '#FFF' : colors.textMuted,
                      borderColor: sel ? colors.accent : colors.border,
                    }}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Notes" colors={colors}>
            <textarea data-testid="mm-notes" value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Optional (client, project, etc.)" rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
              style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }} />
          </Field>
        </form>

        <div className="px-5 py-4 border-t flex justify-end gap-2"
          style={{ borderColor: colors.border }}>
          <button onClick={onClose} type="button" data-testid="mm-cancel"
            className="px-4 py-2 rounded-lg text-sm font-semibold border"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}>
            Cancel
          </button>
          <button onClick={submit} disabled={!isValid || saving} data-testid="mm-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: colors.accent, opacity: (!isValid || saving) ? 0.5 : 1 }}>
            <Plus size={14} /> {saving ? 'Saving…' : 'Save expense'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, colors }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-1"
        style={{ color: colors.textMuted }}>{label}</p>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, colors, testID }) {
  return (
    <div className="relative flex items-center rounded-lg"
      style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
      <span className="pl-3 text-sm" style={{ color: colors.textMuted }}>$</span>
      <input data-testid={testID} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="0.00" inputMode="decimal"
        className="flex-1 px-2 py-2 bg-transparent text-sm outline-none tabular-nums"
        style={{ color: colors.text }} />
    </div>
  );
}
